import { z } from 'zod';

// QuickBooks connection status for the Settings panel (T-065). `configured` =
// server has client id/secret; `connected` = a stored OAuth connection exists.
export const QbStatusResponse = z.object({
  connected: z.boolean(),
  configured: z.boolean(),
  realm_id: z.string().optional(),
  environment: z.enum(['sandbox', 'production']).optional(),
  access_token_expires_at: z.string().optional(),
  refresh_token_expires_at: z.string().nullable().optional(),
});
export type QbStatusResponse = z.infer<typeof QbStatusResponse>;

// ── Link to QuickBooks (T-089) ───────────────────────────────────────
// Manual, vendor-scoped linking: connect a portal vendor + its engagements'
// services to QuickBooks entities created by hand. Link-only — never creates.

export const QbSearchResult = z.object({
  id: z.string(),
  name: z.string(),
});
export type QbSearchResult = z.infer<typeof QbSearchResult>;

export const QbSearchResponse = z.object({
  results: z.array(QbSearchResult),
});
export type QbSearchResponse = z.infer<typeof QbSearchResponse>;

// One engagement = one QuickBooks Service item. qb_service_id null = unlinked.
export const QbLinkService = z.object({
  engagement_id: z.string().uuid(),
  product_service: z.string(),
  resource_name: z.string(),
  qb_service_id: z.string().nullable(),
});
export type QbLinkService = z.infer<typeof QbLinkService>;

export const QbLinkState = z.object({
  connected: z.boolean(),
  vendor: z.object({
    vendor_id: z.string().uuid(),
    business_name: z.string(),
    qb_vendor_id: z.string().nullable(),
  }),
  services: z.array(QbLinkService),
});
export type QbLinkState = z.infer<typeof QbLinkState>;

export const QbLinkVendorInput = z.object({ qb_vendor_id: z.string().min(1) });
export type QbLinkVendorInput = z.infer<typeof QbLinkVendorInput>;

export const QbLinkServiceInput = z.object({ qb_service_id: z.string().min(1) });
export type QbLinkServiceInput = z.infer<typeof QbLinkServiceInput>;

// ── Bulk link to QuickBooks (T-205) ──────────────────────────────────
// GET  /api/quickbooks/link-status      → QbLinkStatusResponse (DB-only; drives the banner)
// GET  /api/quickbooks/link-candidates  → QbLinkCandidatesResponse (read-only dry run against
//        QuickBooks; optional ?vendor_id= scopes it to one vendor + its services). 409
//        quickbooks_not_connected when QB isn't connected.
// POST /api/quickbooks/link-bulk        → QbBulkLinkResponse (link-only: never writes to QB)

export const QbLinkStatusResponse = z.object({
  connected: z.boolean(),
  unlinked_vendors: z.number().int().nonnegative(),
  unlinked_services: z.number().int().nonnegative(),
});
export type QbLinkStatusResponse = z.infer<typeof QbLinkStatusResponse>;

// exact = identical name (pre-selected); suggested = same name modulo case/punctuation/
// LLC-Inc-style suffix (opt-in); none = no single safe match.
export const QbLinkMatch = z.enum(['exact', 'suggested', 'none']);
export type QbLinkMatch = z.infer<typeof QbLinkMatch>;

export const QbLinkCandidate = z.object({
  kind: z.enum(['vendor', 'service']),
  /** vendor_id for a vendor, engagement_id for a service. */
  id: z.string().uuid(),
  name: z.string(),
  /** The portal vendor this row belongs to (a vendor row: its own id) — for "Pick manually…". */
  vendor_id: z.string().uuid(),
  /** Services: the vendor they bill under. Null for a vendor row. */
  vendor_name: z.string().nullable(),
  match: QbLinkMatch,
  qb_id: z.string().nullable(),
  qb_name: z.string().nullable(),
  /** Why a suggestion differs, or why there is no match (e.g. already linked elsewhere). */
  note: z.string().nullable(),
});
export type QbLinkCandidate = z.infer<typeof QbLinkCandidate>;

export const QbLinkCandidatesResponse = z.object({
  vendors: z.array(QbLinkCandidate),
  services: z.array(QbLinkCandidate),
});
export type QbLinkCandidatesResponse = z.infer<typeof QbLinkCandidatesResponse>;

export const QbBulkLinkInput = z.object({
  vendors: z
    .array(z.object({ vendor_id: z.string().uuid(), qb_vendor_id: z.string().min(1) }))
    .max(500)
    .default([]),
  services: z
    .array(z.object({ engagement_id: z.string().uuid(), qb_service_id: z.string().min(1) }))
    .max(500)
    .default([]),
});
export type QbBulkLinkInput = z.infer<typeof QbBulkLinkInput>;

export const QbBulkLinkResponse = z.object({
  linked_vendors: z.number().int().nonnegative(),
  linked_services: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({ kind: z.enum(['vendor', 'service']), id: z.string().uuid(), reason: z.string() }),
  ),
});
export type QbBulkLinkResponse = z.infer<typeof QbBulkLinkResponse>;

// ── Billing push pre-flight + QuickBooks-wins rates (T-205 / T-210) ──
// The push's checks run without calling QuickBooks for the link holds; rate checks read the
// Service items' cost / price best-effort. Carried on the /api/billing/preview response as
// `qb_readiness` (null when the qb_bill_push flag is off).
//
// QuickBooks is the source of truth for BOTH rates: the item's PurchaseCost is the contractor
// rate (the bill line's rate), its UnitPrice is the client rate. The portal's rate_to_contractor
// / rate_to_client mirror them. rate_to_client is OWNER-ONLY (T-186/T-194): its entries, values
// and percent changes are omitted for non-owners — only `hidden_client_rates` counts remain.

export const RateField = z.enum(['rate_to_contractor', 'rate_to_client']);
export type RateField = z.infer<typeof RateField>;

/** A change beyond ±this percent (vs the portal rate) HOLDS the bill until it is confirmed. */
export const RATE_CHANGE_HOLD_PCT = 25;

export const QbReadinessResource = z.object({
  resource_id: z.string().uuid(),
  worker: z.string(),
});

export const QbReadiness = z.object({
  total_resources: z.number().int().nonnegative(),
  // Held resources grouped by vendor + reason. `resources` lets the UI skip an individual one.
  held: z.array(
    z.object({
      vendor_id: z.string().uuid(),
      vendor: z.string(),
      reason: z.string(),
      /** True when "Link all to QuickBooks" can fix this hold (vendor/service not linked yet). */
      linkable: z.boolean(),
      resource_count: z.number().int().positive(),
      resources: z.array(QbReadinessResource),
    }),
  ),
  /**
   * True only when every rate that needed checking was read from QuickBooks. False means the
   * card must NOT claim "all clear": either QuickBooks is not connected, or `rates_unchecked`
   * resources couldn't be checked (throttled / unreachable) — link holds are still reported.
   */
  rates_checked: z.boolean(),
  /** Resources whose rates couldn't be checked this time (QuickBooks throttled or unreachable). */
  rates_unchecked: z.number().int().nonnegative(),
  /** QuickBooks differs from the portal. `hold` = beyond ±25%, the bill is held until confirmed. */
  rate_changes: z.array(
    z.object({
      resource_id: z.string().uuid(),
      engagement_id: z.string().uuid(),
      worker: z.string(),
      vendor: z.string(),
      field: RateField,
      portal_rate: z.number(),
      qb_rate: z.number(),
      change_pct: z.number(),
      hold: z.boolean(),
      /** The resource's bill for this period is already in QuickBooks (it was billed at qb_rate). */
      pushed: z.boolean(),
    }),
  ),
  /** Portal rate blank; it will be filled from QuickBooks when the push runs. */
  rate_fills: z.array(
    z.object({
      resource_id: z.string().uuid(),
      engagement_id: z.string().uuid(),
      worker: z.string(),
      vendor: z.string(),
      field: RateField,
      qb_rate: z.number(),
    }),
  ),
  /** rate_to_client entries withheld from a non-owner admin — counts only, never values. */
  hidden_client_rates: z.object({
    changes: z.number().int().nonnegative(),
    fills: z.number().int().nonnegative(),
  }),
  /** Marked "handled manually" for this period (no push, no hold). */
  skipped: z.array(
    z.object({
      resource_id: z.string().uuid(),
      worker: z.string(),
      vendor: z.string(),
      note: z.string().nullable(),
    }),
  ),
});
export type QbReadiness = z.infer<typeof QbReadiness>;

// POST /api/billing/confirm-rates (admin; rate_to_client rows need the owner). The server
// re-reads the live QuickBooks value and only applies a row whose qb_rate still matches.
export const QbConfirmRatesInput = z.object({
  confirmations: z
    .array(
      z.object({
        engagement_id: z.string().uuid(),
        field: RateField,
        qb_rate: z.number().positive(),
      }),
    )
    .min(1)
    .max(500),
});
export type QbConfirmRatesInput = z.infer<typeof QbConfirmRatesInput>;

export const QbConfirmRatesResponse = z.object({
  confirmed: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({ engagement_id: z.string().uuid(), field: RateField, reason: z.string() }),
  ),
});
export type QbConfirmRatesResponse = z.infer<typeof QbConfirmRatesResponse>;

// POST /api/billing/skip-resource and /unskip-resource (admin): "I'll do this one manually".
// A skip is recorded in the push ledger for (period, resource), so a later push never creates a
// second bill for it. Unskip removes ONLY a manual skip, never a real push record.
export const QbSkipResourceInput = z.object({
  period: z.string().min(1),
  resource_id: z.string().uuid(),
  note: z.string().trim().max(500).optional(),
});
export type QbSkipResourceInput = z.infer<typeof QbSkipResourceInput>;

export const QbUnskipResourceInput = z.object({
  period: z.string().min(1),
  resource_id: z.string().uuid(),
});
export type QbUnskipResourceInput = z.infer<typeof QbUnskipResourceInput>;

// POST /api/billing/push-to-qb response (also carried by the 409 quickbooks_not_connected and
// 500 push_interrupted bodies). Client-rate values/percent appear only for an owner.
export const QbPushSummary = z.object({
  period: z.string(),
  pushed: z.number().int().nonnegative(),
  already_pushed: z.number().int().nonnegative(),
  held: z.array(z.object({ resourceId: z.string(), worker: z.string(), reason: z.string() })),
  failed: z.array(
    z.object({
      resourceId: z.string(),
      worker: z.string(),
      vendor: z.string().nullable(),
      reason: z.string(),
      intuitTid: z.string().nullable(),
      qbBillId: z.string().optional(),
    }),
  ),
  aborted: z
    .object({
      reason: z.enum([
        'quickbooks_not_connected',
        'rate_limited',
        'terms_not_found',
        'internal_error',
      ]),
      notAttempted: z.number().int().nonnegative(),
    })
    .nullable(),
  /** Portal rates filled from QuickBooks by this push. */
  filled: z.array(
    z.object({
      resourceId: z.string(),
      engagementId: z.string().uuid(),
      worker: z.string(),
      field: RateField,
      qbRate: z.number(),
    }),
  ),
  /** Rates that differ from QuickBooks but within ±25%: billed at QuickBooks' rate, awaiting confirmation. */
  changed: z.array(
    z.object({
      resourceId: z.string(),
      /** Lets the push result confirm the change inline (POST /api/billing/confirm-rates). */
      engagementId: z.string().uuid(),
      worker: z.string(),
      field: RateField,
      portalRate: z.number(),
      qbRate: z.number(),
      changePct: z.number(),
    }),
  ),
  /** Resources already marked "handled manually" for this period. */
  skipped_manually: z.array(z.object({ resourceId: z.string(), worker: z.string() })),
  hidden_client_rates: z.object({
    filled: z.number().int().nonnegative(),
    changed: z.number().int().nonnegative(),
  }),
});
export type QbPushSummary = z.infer<typeof QbPushSummary>;
